// Shared request/response Zod schemas for POST /api/v1/migration/authenticate —
// the server half of the Pelago 1.0 keychain handoff sign-in. The `migration`
// Lambda re-exports these from its types layer (ADR-022) and the mobile client
// imports the same request schema, so the wire contract has one owner.
//
// Wire fields are camelCase: the request IS the client's validated keychain
// record (minus the fields the server ignores), and the response shape is
// fixed by the migration contract page — this surface deliberately does not
// follow the snake_case convention of the neighbouring /api/v1/auth routes.

import { z } from 'zod';
import { apiEnvelope } from '../../envelope';
import { memberIdSchema } from '../../identity/legacy-identity-id';
import {
  MEMBER_MIGRATION_STATUS,
  memberMigrationStatusSchema,
  memberMigrationStepStatusSchema,
} from '../../member-migration/schema';

/** A legacy Cognito JWT as carried in the keychain; bounded so a hostile body cannot carry megabytes. */
const legacyTokenSchema = z.string().min(1).max(8192);

/**
 * The legacy pool coordinates the handoff record names. The server never
 * trusts them as an issuer choice: they are compared against the pools the
 * environment configured and anything else is refused.
 */
export const migrationAuthenticatePoolSchema = z.object({
  poolId: z.string().min(1).max(128),
  clientId: z.string().min(1).max(128),
  region: z.string().min(1).max(32),
});
export type MigrationAuthenticatePool = z.infer<typeof migrationAuthenticatePoolSchema>;

// Deliberately NOT `.strict()` at either level (ADR-135): the client may post
// its whole validated record — `schemaVersion`, `writtenAt`, `legacyAppVersion`,
// `firstName` — and those fields are ignored server-side by contract.
export const migrationAuthenticateRequestSchema = z.object({
  /** The keychain id token — usually expired; never trusted alone. */
  idToken: legacyTokenSchema,
  refreshToken: legacyTokenSchema,
  pool: migrationAuthenticatePoolSchema,
  nonce: z.string().uuid(),
});
export type MigrationAuthenticateRequest = z.infer<typeof migrationAuthenticateRequestSchema>;

export const migrationAuthenticateTokensSchema = z.object({
  accessToken: z.string().min(1),
  idToken: z.string().min(1),
  refreshToken: z.string().min(1),
  expiresIn: z.number().int().positive(),
});
export type MigrationAuthenticateTokens = z.infer<typeof migrationAuthenticateTokensSchema>;

export const migrationAuthenticateMigrationSchema = z.object({
  status: memberMigrationStatusSchema,
  /** False once the member's Tier 0 marker is `done`; the client then skips `/migration/start`. */
  startRequired: z.boolean(),
});
export type MigrationAuthenticateMigration = z.infer<typeof migrationAuthenticateMigrationSchema>;

export const migrationAuthenticateDataSchema = z.object({
  /** Equals the member's legacy identity id. */
  // A migrated member's id is backend2's GUID, not necessarily a v4 UUID.
  memberId: memberIdSchema,
  tokens: migrationAuthenticateTokensSchema,
  migration: migrationAuthenticateMigrationSchema,
});
export type MigrationAuthenticateData = z.infer<typeof migrationAuthenticateDataSchema>;
export const migrationAuthenticateResponseSchema = apiEnvelope(migrationAuthenticateDataSchema);

/**
 * Closed error vocabulary of the authenticate route (ADR-002 Rule 4). The
 * client branches on these codes: 400/401 fall back to OTP, 403 shows the
 * not-found / disabled copy, 503 retries once then falls back, 409 shows a
 * retry and is never read as "already migrated" (that case is a 200).
 */
export const MIGRATION_ERROR_CODES = [
  'invalid_request',
  'legacy_credentials_invalid',
  'legacy_account_disabled',
  'legacy_identity_not_found',
  'member_id_exists',
  'member_disabled',
  'rate_limited',
  'not_enabled',
  'legacy_pool_unavailable',
  'legacy_identity_unavailable',
  'internal_error',
  // Appended, never reordered: the start and status routes share this closed
  // vocabulary and a client shipped against the original order still resolves
  // every code it knows by name.
  'invalid_token',
  'not_migrating',
] as const;
export type MigrationErrorCode = (typeof MIGRATION_ERROR_CODES)[number];
export const migrationErrorCodeSchema = z.enum(MIGRATION_ERROR_CODES);

export const MIGRATION_ERROR_HTTP_STATUS = {
  invalid_request: 400,
  legacy_credentials_invalid: 401,
  legacy_account_disabled: 403,
  legacy_identity_not_found: 403,
  member_id_exists: 409,
  member_disabled: 403,
  rate_limited: 429,
  not_enabled: 503,
  legacy_pool_unavailable: 503,
  legacy_identity_unavailable: 503,
  internal_error: 500,
  invalid_token: 401,
  not_migrating: 404,
} as const satisfies Record<MigrationErrorCode, number>;

/** PII-free client-facing message per code; diagnostics stay in server logs. */
export const MIGRATION_ERROR_PUBLIC_MESSAGE: Record<MigrationErrorCode, string> = {
  invalid_request: 'The request is invalid.',
  legacy_credentials_invalid: 'The previous session could not be verified.',
  legacy_account_disabled: 'This account is disabled.',
  legacy_identity_not_found: 'No account was found for this sign-in.',
  member_id_exists: 'A member with this id already exists.',
  member_disabled: 'This account is disabled.',
  rate_limited: 'Too many requests. Please try again later.',
  not_enabled: 'Account transfer is not available right now.',
  legacy_pool_unavailable: 'The previous sign-in service is temporarily unavailable.',
  legacy_identity_unavailable: 'The account lookup service is temporarily unavailable.',
  internal_error: 'An internal error occurred.',
  invalid_token: 'Missing or invalid bearer token.',
  not_migrating: 'No account transfer is in progress for this member.',
};

// ── POST /api/v1/migration/start ─────────────────────────────────────────────
//
// The call carries NO input: the member is the bearer token and nothing in the
// body is read. This schema exists to DOCUMENT that in the OpenAPI contract;
// the handler never parses the body, so a request with no body at all, an
// empty object, or a stray field is equally acceptable.
export const migrationStartRequestSchema = z.object({}).passthrough();
export type MigrationStartRequest = z.infer<typeof migrationStartRequestSchema>;

/**
 * Why a transfer stopped, as a closed vocabulary the client and the operator
 * runbook share. Distinct from `MIGRATION_ERROR_CODES`: an error code is an
 * HTTP refusal made BEFORE the migration marker was touched, a failure reason
 * is the state the marker was left in, and the response for the latter is a
 * 200 so the client renders one shape either way.
 */
export const MIGRATION_FAILURE_REASONS = [
  'legacy_identity_not_found',
  'legacy_identity_unresolved',
  'legacy_identity_mismatch',
  'legacy_account_foreign_marker',
  'legacy_account_archived_other',
  'legacy_freeze_unavailable',
  'legacy_source_unavailable',
  'legacy_source_rejected',
  'source_ref_owned_by_other_member',
  'timeout',
  'internal_error',
] as const;
export type MigrationFailureReason = (typeof MIGRATION_FAILURE_REASONS)[number];
export const migrationFailureReasonSchema = z.enum(MIGRATION_FAILURE_REASONS);

/**
 * The reasons a transfer must never be retried automatically: each one means
 * the legacy identity is contested, unresolvable, or deliberately archived, so
 * a retry would either import the wrong person or loop. A human decides.
 */
export const MIGRATION_HARD_STOP_REASONS = [
  'legacy_identity_unresolved',
  'legacy_identity_mismatch',
  'legacy_account_foreign_marker',
  'legacy_account_archived_other',
  'source_ref_owned_by_other_member',
] as const;
export type MigrationHardStopReason = (typeof MIGRATION_HARD_STOP_REASONS)[number];
const HARD_STOP_REASONS: ReadonlySet<string> = new Set(MIGRATION_HARD_STOP_REASONS);
export function isMigrationHardStopReason(
  reason: MigrationFailureReason | null,
): reason is MigrationHardStopReason {
  return reason !== null && HARD_STOP_REASONS.has(reason);
}

/** One recorded transfer step, in the order the run performs them. */
export const migrationStepReportSchema = z.object({
  name: z.string().min(1),
  status: memberMigrationStepStatusSchema,
  /**
   * The step's own classification — a bare code, or `code:value` for the
   * codes that name an unmapped 1.0 value. Null while the step is settled
   * without one.
   */
  reason: z.string().nullable(),
});
export type MigrationStepReport = z.infer<typeof migrationStepReportSchema>;

// Deliberately NOT `.strict()` (ADR-135): a later slice adding a field must not
// break a client shipped against this shape.
export const migrationStartDataSchema = z.object({
  status: memberMigrationStatusSchema,
  tier0CompletedAt: z.string().datetime().nullable(),
  reason: migrationFailureReasonSchema.nullable(),
  /** False for a completed transfer and for every hard stop; true otherwise. */
  retryable: z.boolean(),
  steps: z.array(migrationStepReportSchema),
});
export type MigrationStartData = z.infer<typeof migrationStartDataSchema>;
export const migrationStartResponseSchema = apiEnvelope(migrationStartDataSchema);

// ── GET /api/v1/migration/status ─────────────────────────────────────────────
//
// The minimal poll surface the app needs before the Sona conversation: does the
// table say Tier 1 is done. No per-step detail and no retry semantics — that
// fuller shape is Phase 2.
export const MIGRATION_STATUS_STATES = ['not_migrating', ...MEMBER_MIGRATION_STATUS] as const;
export type MigrationStatusState = (typeof MIGRATION_STATUS_STATES)[number];
export const migrationStatusStateSchema = z.enum(MIGRATION_STATUS_STATES);

export const migrationStatusDataSchema = z.object({
  status: migrationStatusStateSchema,
  tier0Complete: z.boolean(),
  tier1Complete: z.boolean(),
});
export type MigrationStatusData = z.infer<typeof migrationStatusDataSchema>;
export const migrationStatusResponseSchema = apiEnvelope(migrationStatusDataSchema);

export * from './summary';
